import { api } from '../lib/api';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Download, Play, Pause, CheckCircle, Mail, MessageSquare, Users, Calendar, Clock } from 'lucide-react';
import CampaignStatusBadge from '../components/campaigns/CampaignStatusBadge';

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [nextStatus, setNextStatus] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Step', 'Status', 'Last Sent', 'Message'];
    const rows = patients.map(entry => {
      const name = entry.patient?.name || 'Unnamed';
      const email = entry.patient?.email || '—';
      const step = `Step ${entry.current_step + 1} of ${campaign?.total_steps || 3}`;
      const status = entry.completed ? 'Completed' : entry.replied ? 'Replied' : 'Active';
      const lastSent = entry.last_sent ? new Date(entry.last_sent).toLocaleString() : '—';
      const message = entry.message ? entry.message.replace(/\n/g, ' ').replace(/"/g, '""') : '';
      return `"${name}","${email}","${step}","${status}","${lastSent}","${message}"`;
    });

    const csvContent = `data:text/csv;charset=utf-8,${headers.join(',')}\n${rows.join('\n')}`;
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `${campaign.name || 'campaign'}_patients.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleStatusChange = (status) => {
    setNextStatus(status);
    setShowStatusModal(true);
  };

  const updateCampaignStatus = async () => {
    if (!nextStatus) return;
    setIsUpdatingStatus(true);
    try {
      await api.updateCampaign(id, { status: nextStatus });
      setCampaign(prev => ({ ...prev, status: nextStatus }));
      toast.success('Campaign status updated');
    } catch (error) {
      toast.error('Something went wrong while updating status');
    }
    setIsUpdatingStatus(false);
    setShowStatusModal(false);
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [campaignData, patientsData] = await Promise.all([
          api.getCampaign(id),
          api.getCampaignPatients(id),
        ]);

        setCampaign(campaignData);
        setPatients(patientsData.patients || []);
      } catch (error) {
        console.error('Error loading campaign data:', error);
        toast.error('Error loading campaign');
      }
      setLoading(false);
    }
    fetchData();
  }, [id]);

  const getChannelIcon = (channel) => {
    return channel === 'sms' ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />;
  };

  const getPatientStatus = (entry) => {
    if (entry.completed) return { label: 'Completed', color: 'text-emerald-600', bg: 'bg-emerald-50' };
    if (entry.replied) return { label: 'Replied', color: 'text-blue-600', bg: 'bg-blue-50' };
    return { label: 'Pending', color: 'text-slate-600', bg: 'bg-slate-50' };
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-slate-100 rounded" />
          <div className="h-32 bg-slate-100 rounded-xl" />
          <div className="h-64 bg-slate-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-slate-500">Campaign not found</p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/campaigns')}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Campaigns
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-semibold text-slate-900">{campaign.name}</h1>
                <CampaignStatusBadge status={campaign.status} />
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  {getChannelIcon(campaign.channel)}
                  {campaign.channel === 'sms' ? 'SMS' : 'Email'}
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  {patients.length} patients
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  {new Date(campaign.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        {(campaign.status === 'active' || campaign.status === 'paused' || campaign.status === 'draft') && (
          <div className="flex gap-2 mb-6">
            {campaign.status === 'active' && (
              <>
                <button
                  onClick={() => handleStatusChange('paused')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Pause className="h-4 w-4" />
                  Pause
                </button>
                <button
                  onClick={() => handleStatusChange('completed')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <CheckCircle className="h-4 w-4" />
                  Mark Complete
                </button>
              </>
            )}
            {campaign.status === 'paused' && (
              <button
                onClick={() => handleStatusChange('active')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <Play className="h-4 w-4" />
                Resume
              </button>
            )}
            {campaign.status === 'draft' && (
              <button
                onClick={() => handleStatusChange('active')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <Play className="h-4 w-4" />
                Start Campaign
              </button>
            )}
          </div>
        )}

        {/* Patients List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-sm font-medium text-slate-900">Patients in Campaign</h2>
          </div>

          {patients.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No patients in this campaign</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {patients.map((entry) => {
                const status = getPatientStatus(entry);
                return (
                  <div key={entry.id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium text-slate-900">
                            {entry.patient?.name || 'Unnamed Patient'}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500">
                          {entry.patient?.email || entry.patient?.phone || 'No contact info'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          Step {(entry.current_step ?? 0) + 1} of {campaign?.total_steps || 1}
                        </span>
                      </div>
                    </div>

                    {entry.last_sent && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
                        <Clock className="h-3 w-3" />
                        Last sent: {new Date(entry.last_sent).toLocaleString()}
                      </div>
                    )}

                    {entry.message && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600 italic">"{entry.message}"</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Confirm Status Change</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-slate-600">
                Are you sure you want to set this campaign as <strong className="text-slate-900">{nextStatus}</strong>?
              </p>
            </div>
            <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateCampaignStatus}
                disabled={isUpdatingStatus}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors"
              >
                {isUpdatingStatus ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CampaignDetail;
